import { useState, useEffect } from 'react';
import { Plus, Search, Lock, Globe, Edit2, Trash2, X, Loader2, Copy, Check, Ban, Store, Save } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useShops } from '../contexts/ShopContext';
import { Button, Input } from '../components/ui';
import { getPrompts, createPrompt, updatePrompt, deletePrompt, Prompt } from '../services/promptService';
import { getAllNegativePrompts, saveNegativePrompt, NegativePrompt } from '../services/negativePromptService';
import { useNavigate } from 'react-router-dom';
import './PromptsPage.css';

export function PromptsPage() {
    const { userProfile, isAdmin } = useAuth();
    const { shops } = useShops();
    const navigate = useNavigate();

    // Tab state
    const [activeTab, setActiveTab] = useState<'library' | 'negative'>('library');

    const [searchQuery, setSearchQuery] = useState('');
    const [filter, setFilter] = useState<'all' | 'public' | 'private'>('all');
    const [prompts, setPrompts] = useState<Prompt[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null);
    const [modalTitle, setModalTitle] = useState('');
    const [modalContent, setModalContent] = useState('');
    const [modalType, setModalType] = useState<'public' | 'private'>('private');
    const [modalUseProductContext, setModalUseProductContext] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // Delete confirmation
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deletingPrompt, setDeletingPrompt] = useState<Prompt | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Copy feedback
    const [copiedId, setCopiedId] = useState<string | null>(null);

    // Negative prompts state
    const [negativePrompts, setNegativePrompts] = useState<NegativePrompt[]>([]);
    const [isLoadingNegative, setIsLoadingNegative] = useState(false);
    const [editingNegativeShopId, setEditingNegativeShopId] = useState<string | null>(null);
    const [negativePromptContent, setNegativePromptContent] = useState('');
    const [isSavingNegative, setIsSavingNegative] = useState(false);

    useEffect(() => {
        loadPrompts();
    }, [userProfile?.uid]);

    useEffect(() => {
        if (activeTab === 'negative' && shops.length > 0) {
            loadNegativePrompts();
        }
    }, [activeTab, shops]);

    const loadPrompts = async () => {
        if (!userProfile?.uid) return;

        setIsLoading(true);
        setError('');

        try {
            const data = await getPrompts(userProfile.uid);
            setPrompts(data);
        } catch (err: any) {
            console.error('Error loading prompts:', err);
            setError('Erreur lors du chargement des prompts');
        } finally {
            setIsLoading(false);
        }
    };

    const loadNegativePrompts = async () => {
        console.log('[NegativePrompts] Loading, shops:', shops.length);
        setIsLoadingNegative(true);
        setError('');

        try {
            const existing = await getAllNegativePrompts();
            console.log('[NegativePrompts] Existing prompts from Firestore:', existing);
            // Merge with shops - show all shops, with their negative prompt if exists
            const mergedPrompts: NegativePrompt[] = shops.map(shop => {
                const existingPrompt = existing.find(p => p.shopId === shop.id);
                return existingPrompt || {
                    shopId: shop.id,
                    shopName: shop.name,
                    content: '',
                    updatedAt: new Date(),
                };
            });
            console.log('[NegativePrompts] Merged prompts:', mergedPrompts);
            setNegativePrompts(mergedPrompts);
        } catch (err: any) {
            console.error('Error loading negative prompts:', err);
            setError('Erreur lors du chargement des prompts négatifs');
        } finally {
            setIsLoadingNegative(false);
        }
    };

    const handleEditNegativePrompt = (shopId: string, currentContent: string) => {
        setEditingNegativeShopId(shopId);
        setNegativePromptContent(currentContent);
    };

    const handleSaveNegativePrompt = async (shopId: string, shopName: string) => {
        setIsSavingNegative(true);
        try {
            await saveNegativePrompt(shopId, shopName, negativePromptContent, userProfile?.displayName);
            setEditingNegativeShopId(null);
            setNegativePromptContent('');
            await loadNegativePrompts();
        } catch (err: any) {
            console.error('Error saving negative prompt:', err);
            setError('Erreur lors de la sauvegarde');
        } finally {
            setIsSavingNegative(false);
        }
    };

    const handleCancelEditNegative = () => {
        setEditingNegativeShopId(null);
        setNegativePromptContent('');
    };

    const filteredPrompts = prompts.filter((prompt) => {
        // Filter by type
        if (filter === 'public' && prompt.type !== 'public') return false;
        if (filter === 'private' && (prompt.type !== 'private' || prompt.ownerId !== userProfile?.uid)) return false;

        // Search filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            return (
                prompt.title.toLowerCase().includes(query) ||
                prompt.content.toLowerCase().includes(query)
            );
        }

        return true;
    });

    const openCreateModal = () => {
        setEditingPrompt(null);
        setModalTitle('');
        setModalContent('');
        setModalType('private');
        setModalUseProductContext(true);
        setShowModal(true);
    };

    const openEditModal = (prompt: Prompt) => {
        setEditingPrompt(prompt);
        setModalTitle(prompt.title);
        setModalContent(prompt.content);
        setModalType(prompt.type);
        setModalUseProductContext(prompt.useProductContext ?? true);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingPrompt(null);
        setModalTitle('');
        setModalContent('');
        setError('');
    };

    const handleSave = async () => {
        if (!modalTitle.trim() || !modalContent.trim()) {
            setError('Le titre et le contenu sont requis');
            return;
        }

        if (!userProfile?.uid) return;

        setIsSaving(true);
        setError('');

        try {
            if (editingPrompt) {
                await updatePrompt(editingPrompt.id, {
                    title: modalTitle.trim(),
                    content: modalContent.trim(),
                    type: modalType,
                    useProductContext: modalUseProductContext,
                });
            } else {
                await createPrompt(
                    {
                        title: modalTitle.trim(),
                        content: modalContent.trim(),
                        type: modalType,
                        useProductContext: modalUseProductContext,
                    },
                    userProfile.uid,
                    userProfile.displayName || 'Utilisateur'
                );
            }

            closeModal();
            await loadPrompts();
        } catch (err: any) {
            console.error('Error saving prompt:', err);
            setError(err.message || 'Erreur lors de la sauvegarde');
        } finally {
            setIsSaving(false);
        }
    };

    const openDeleteConfirm = (prompt: Prompt) => {
        setDeletingPrompt(prompt);
        setShowDeleteConfirm(true);
    };

    const handleDelete = async () => {
        if (!deletingPrompt) return;

        setIsDeleting(true);

        try {
            await deletePrompt(deletingPrompt.id);
            setShowDeleteConfirm(false);
            setDeletingPrompt(null);
            await loadPrompts();
        } catch (err: any) {
            console.error('Error deleting prompt:', err);
            setError('Erreur lors de la suppression');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleUsePrompt = (prompt: Prompt) => {
        // Navigate to generator with prompt content
        navigate('/generator', { state: { promptContent: prompt.content } });
    };

    const handleCopyPrompt = async (prompt: Prompt) => {
        try {
            await navigator.clipboard.writeText(prompt.content);
            setCopiedId(prompt.id);
            setTimeout(() => setCopiedId(null), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const canEditPrompt = (prompt: Prompt) => {
        return prompt.ownerId === userProfile?.uid || isAdmin;
    };

    return (
        <div className="prompts-page animate-fadeIn">
            <header className="page-header">
                <div>
                    <h1 className="page-title">Bibliothèque de Prompts</h1>
                    <p className="page-subtitle">Gérez vos prompts pour la génération d'images</p>
                </div>
                {activeTab === 'library' && (
                    <Button leftIcon={<Plus size={18} />} onClick={openCreateModal}>
                        Nouveau prompt
                    </Button>
                )}
            </header>

            {/* Tab Navigation */}
            <div className="prompts-tabs glass">
                <button
                    className={`tab-btn ${activeTab === 'library' ? 'active' : ''}`}
                    onClick={() => setActiveTab('library')}
                >
                    <Globe size={16} />
                    Bibliothèque
                </button>
                <button
                    className={`tab-btn ${activeTab === 'negative' ? 'active' : ''}`}
                    onClick={() => setActiveTab('negative')}
                >
                    <Ban size={16} />
                    Prompts Négatifs
                </button>
            </div>

            {activeTab === 'library' ? (
                <>
                    <div className="prompts-toolbar glass">
                        <Input
                            placeholder="Rechercher un prompt..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            leftIcon={<Search size={18} />}
                            className="search-input"
                        />

                        <div className="filter-buttons">
                            <button
                                className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                                onClick={() => setFilter('all')}
                            >
                                Tous
                            </button>
                            <button
                                className={`filter-btn ${filter === 'public' ? 'active' : ''}`}
                                onClick={() => setFilter('public')}
                            >
                                <Globe size={14} /> Publics
                            </button>
                            <button
                                className={`filter-btn ${filter === 'private' ? 'active' : ''}`}
                                onClick={() => setFilter('private')}
                            >
                                <Lock size={14} /> Privés
                            </button>
                        </div>
                    </div>

                    {isLoading ? (
                        <div className="loading-state">
                            <Loader2 size={32} className="spin" />
                            <p>Chargement des prompts...</p>
                        </div>
                    ) : error && prompts.length === 0 ? (
                        <div className="error-state glass">
                            <p>{error}</p>
                            <Button variant="secondary" onClick={loadPrompts}>Réessayer</Button>
                        </div>
                    ) : filteredPrompts.length === 0 ? (
                        <div className="empty-state glass">
                            <Lock size={64} className="empty-icon" />
                            <h2>{searchQuery ? 'Aucun résultat' : 'Aucun prompt'}</h2>
                            <p>
                                {searchQuery
                                    ? 'Aucun prompt ne correspond à votre recherche.'
                                    : 'Créez votre premier prompt pour commencer à générer des images plus rapidement.'}
                            </p>
                            {!searchQuery && (
                                <Button leftIcon={<Plus size={18} />} onClick={openCreateModal}>
                                    Créer un prompt
                                </Button>
                            )}
                        </div>
                    ) : (
                        <div className="prompts-grid">
                            {filteredPrompts.map((prompt) => (
                                <div key={prompt.id} className="prompt-card glass">
                                    <div className="prompt-header">
                                        <span className={`prompt-badge badge-${prompt.type}`}>
                                            {prompt.type === 'public' ? <Globe size={12} /> : <Lock size={12} />}
                                            {prompt.type === 'public' ? 'Public' : 'Privé'}
                                        </span>
                                        <div className="prompt-actions">
                                            <button
                                                className="action-btn"
                                                onClick={() => handleCopyPrompt(prompt)}
                                                title="Copier"
                                            >
                                                {copiedId === prompt.id ? <Check size={14} /> : <Copy size={14} />}
                                            </button>
                                            {canEditPrompt(prompt) && (
                                                <>
                                                    <button
                                                        className="action-btn"
                                                        onClick={() => openEditModal(prompt)}
                                                        title="Modifier"
                                                    >
                                                        <Edit2 size={14} />
                                                    </button>
                                                    <button
                                                        className="action-btn action-btn-danger"
                                                        onClick={() => openDeleteConfirm(prompt)}
                                                        title="Supprimer"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <h3 className="prompt-title">{prompt.title}</h3>
                                    <p className="prompt-content">{prompt.content}</p>
                                    {prompt.ownerName && prompt.ownerId !== userProfile?.uid && (
                                        <p className="prompt-author">Par {prompt.ownerName}</p>
                                    )}
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        className="use-prompt-btn"
                                        onClick={() => handleUsePrompt(prompt)}
                                    >
                                        Utiliser ce prompt
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Create/Edit Modal */}
                    {showModal && (
                        <div className="modal-overlay" onClick={closeModal}>
                            <div className="modal-content glass" onClick={(e) => e.stopPropagation()}>
                                <div className="modal-header">
                                    <h2>{editingPrompt ? 'Modifier le prompt' : 'Nouveau prompt'}</h2>
                                    <button className="modal-close" onClick={closeModal}>
                                        <X size={20} />
                                    </button>
                                </div>

                                <div className="modal-body">
                                    <div className="form-group">
                                        <label>Titre</label>
                                        <Input
                                            placeholder="Ex: Photo produit sur fond blanc"
                                            value={modalTitle}
                                            onChange={(e) => setModalTitle(e.target.value)}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Contenu du prompt</label>
                                        <textarea
                                            className="prompt-textarea"
                                            placeholder="Décrivez le type d'image à générer..."
                                            value={modalContent}
                                            onChange={(e) => setModalContent(e.target.value)}
                                            rows={5}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Visibilité</label>
                                        <div className="visibility-options">
                                            <button
                                                type="button"
                                                className={`visibility-btn ${modalType === 'private' ? 'active' : ''}`}
                                                onClick={() => setModalType('private')}
                                            >
                                                <Lock size={16} />
                                                <span>Privé</span>
                                                <small>Visible uniquement par vous</small>
                                            </button>
                                            <button
                                                type="button"
                                                className={`visibility-btn ${modalType === 'public' ? 'active' : ''}`}
                                                onClick={() => setModalType('public')}
                                            >
                                                <Globe size={16} />
                                                <span>Public</span>
                                                <small>Visible par tous les utilisateurs</small>
                                            </button>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Enrichissement du contexte</label>
                                        <div className="context-option-modal">
                                            <label className="context-checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={modalUseProductContext}
                                                    onChange={(e) => setModalUseProductContext(e.target.checked)}
                                                    className="context-checkbox"
                                                />
                                                <span>Enrichir avec le contexte du produit</span>
                                            </label>
                                            <small className="context-hint">
                                                Inclut le titre, description, tags et images existantes
                                            </small>
                                        </div>
                                    </div>

                                    {error && <p className="modal-error">{error}</p>}
                                </div>

                                <div className="modal-footer">
                                    <Button variant="secondary" onClick={closeModal}>
                                        Annuler
                                    </Button>
                                    <Button
                                        onClick={handleSave}
                                        isLoading={isSaving}
                                        disabled={!modalTitle.trim() || !modalContent.trim()}
                                    >
                                        {editingPrompt ? 'Enregistrer' : 'Créer'}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Delete Confirmation Modal */}
                    {showDeleteConfirm && deletingPrompt && (
                        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
                            <div className="modal-content modal-sm glass" onClick={(e) => e.stopPropagation()}>
                                <div className="modal-header">
                                    <h2>Supprimer le prompt ?</h2>
                                </div>

                                <div className="modal-body">
                                    <p>Êtes-vous sûr de vouloir supprimer "<strong>{deletingPrompt.title}</strong>" ?</p>
                                    <p className="text-muted">Cette action est irréversible.</p>
                                </div>

                                <div className="modal-footer">
                                    <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
                                        Annuler
                                    </Button>
                                    <Button
                                        variant="primary"
                                        onClick={handleDelete}
                                        isLoading={isDeleting}
                                        className="btn-danger"
                                    >
                                        Supprimer
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            ) : (
                /* Negative Prompts Tab */
                <div className="negative-prompts-section">
                    {shops.length === 0 ? (
                        <div className="empty-state glass">
                            <Store size={64} className="empty-icon" />
                            <h2>Aucune boutique connectée</h2>
                            <p>Connectez une boutique Shopify pour configurer les prompts négatifs.</p>
                        </div>
                    ) : isLoadingNegative ? (
                        <div className="loading-state">
                            <Loader2 size={32} className="spin" />
                            <p>Chargement des prompts négatifs...</p>
                        </div>
                    ) : (
                        <div className="negative-prompts-list">
                            {negativePrompts.map((np) => (
                                <div key={np.shopId} className="negative-prompt-card glass">
                                    <div className="negative-prompt-header">
                                        <div className="shop-info">
                                            <Store size={20} />
                                            <h3>{np.shopName}</h3>
                                        </div>
                                        {editingNegativeShopId !== np.shopId && (
                                            <Button
                                                variant={np.content ? "secondary" : "primary"}
                                                size="sm"
                                                leftIcon={np.content ? <Edit2 size={14} /> : <Plus size={14} />}
                                                onClick={() => handleEditNegativePrompt(np.shopId, np.content)}
                                            >
                                                {np.content ? 'Modifier' : 'Ajouter'}
                                            </Button>
                                        )}
                                    </div>

                                    {editingNegativeShopId === np.shopId ? (
                                        <div className="negative-prompt-editor">
                                            <textarea
                                                className="prompt-textarea"
                                                value={negativePromptContent}
                                                onChange={(e) => setNegativePromptContent(e.target.value)}
                                                placeholder="Éléments à exclure lors de la génération d'images (ex: texte, watermarks, logos, arrière-plans complexes...)"
                                                rows={4}
                                            />
                                            <div className="negative-prompt-actions">
                                                <Button
                                                    variant="secondary"
                                                    size="sm"
                                                    onClick={handleCancelEditNegative}
                                                >
                                                    Annuler
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    leftIcon={<Save size={14} />}
                                                    onClick={() => handleSaveNegativePrompt(np.shopId, np.shopName)}
                                                    isLoading={isSavingNegative}
                                                >
                                                    Enregistrer
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="negative-prompt-content">
                                            {np.content ? (
                                                <p>{np.content}</p>
                                            ) : (
                                                <p className="no-content">Aucun prompt négatif configuré pour cette boutique</p>
                                            )}
                                        </div>
                                    )}

                                    {np.updatedBy && np.content && (
                                        <p className="negative-prompt-meta">
                                            Modifié par {np.updatedBy} le {np.updatedAt.toLocaleDateString('fr-FR')}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
